import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Icon } from "../components/Icons";
import { STUDIO } from "../lib/store";
import { fmtDate } from "../lib/format";

/* The consultant / contractor workspace behind a personal link: only their
   sheets, the RFIs that concern them, and the dates — and their own channel
   BACK: raise an RFI, make a design submission, put in a cost proposal. Still
   revocable, and it never exposes fees, the client's details or anyone else's
   drawings, submissions or prices. */

interface Payload {
  project: { name: string; code: string; type?: string; location?: string; currency?: string };
  partner: { name: string; kind: string; ref: string; disciplines: string[]; note?: string };
  sheets: any[]; rfis: any[]; dates: any[];
  submissions?: any[]; proposals?: any[];
}

export default function PartnerPage() {
  const { token } = useParams();
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<"sheets" | "rfis" | "submissions" | "proposals" | "dates">("sheets");

  async function refresh() {
    try {
      const r = await fetch(`/api/partner/${token}`);
      if (r.status === 403) { setErr("This link has been withdrawn. Please contact the studio."); return; }
      if (!r.ok) { setErr("This link isn't valid. Please check it or contact the studio."); return; }
      setData(await r.json());
    } catch { setErr("Couldn't load the project — check your connection."); }
  }
  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  if (err) return <Shell><div className="card pad" style={{ maxWidth: 460, textAlign: "center" }}><Icon.close width={22} style={{ color: "var(--ink-3)" }} /><div style={{ fontWeight: 600, marginTop: 8 }}>{err}</div></div></Shell>;
  if (!data) return <Shell><div className="dim">Loading…</div></Shell>;

  const byDiscipline = [...new Set(data.sheets.map((s) => s.discipline))];
  const openRfis = data.rfis.filter((r) => r.status !== "closed");

  return (
    <Shell>
      <div style={{ width: "100%", maxWidth: 1000 }}>
        <div className="row between wrap" style={{ gap: 12, marginBottom: 8 }}>
          <div>
            <div className="kicker">{data.partner.kind === "contractor" ? "Contractor access" : "Consultant access"} · {data.partner.ref}</div>
            <h1 className="serif" style={{ fontSize: 34, marginTop: 6 }}>{data.project.name}</h1>
            <div className="muted" style={{ marginTop: 4 }}>{[data.project.code, data.project.type, data.project.location].filter(Boolean).join(" · ")}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13.5, fontWeight: 500 }}>{data.partner.name}</div>
            <div className="dim" style={{ fontSize: 12 }}>{data.partner.disciplines.length ? data.partner.disciplines.join(", ") : "All disciplines"}</div>
          </div>
        </div>
        {data.partner.note && <div className="panel" style={{ padding: "11px 14px", marginBottom: 16, fontSize: 13.5, lineHeight: 1.6 }}>{data.partner.note}</div>}

        <div className="row wrap" style={{ gap: 4, borderBottom: "1px solid var(--line)", marginBottom: 20, marginTop: 16 }}>
          {([["sheets", `Drawings (${data.sheets.length})`], ["rfis", `RFIs (${openRfis.length})`], ["submissions", `My submissions (${(data.submissions ?? []).length})`], ["proposals", `Cost proposals (${(data.proposals ?? []).length})`], ["dates", "Key dates"]] as const).map(([k, label]) => (
            <button key={k} onClick={() => setTab(k as any)} style={{ padding: "11px 15px", background: "none", border: "none", borderBottom: "2px solid " + (tab === k ? "var(--accent)" : "transparent"), color: tab === k ? "var(--ink)" : "var(--ink-3)", fontWeight: 500, fontSize: 14, cursor: "pointer" }}>{label}</button>
          ))}
        </div>

        {tab === "sheets" && (
          data.sheets.length === 0 ? <div className="dim">No drawings shared with you yet.</div> : (
            <div className="col" style={{ gap: 22 }}>
              {byDiscipline.map((disc) => (
                <div key={disc}>
                  <div className="kicker" style={{ marginBottom: 10 }}>{disc}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12 }}>
                    {data.sheets.filter((s) => s.discipline === disc).map((s, i) => (
                      <a key={i} href={s.fileUrl || s.previewUrl} target="_blank" rel="noreferrer" className="card" style={{ overflow: "hidden", display: "block" }}>
                        {s.previewUrl
                          ? <img src={s.previewUrl} alt={s.title} style={{ width: "100%", height: 110, objectFit: "cover", display: "block", background: "#fff" }} />
                          : <div style={{ height: 110, display: "grid", placeItems: "center", color: "var(--ink-3)", fontSize: 12 }}>No preview</div>}
                        <div style={{ padding: "9px 11px" }}>
                          <div className="row between"><span className="mono dim" style={{ fontSize: 10.5 }}>{s.code}</span>{s.revision && <span className="badge" style={{ fontSize: 8.5 }}>{s.revision}</span>}</div>
                          <div style={{ fontSize: 12.5, marginTop: 3 }}>{s.title}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "rfis" && <RaiseRfi token={token!} sheets={data.sheets} onDone={refresh} />}
        {tab === "rfis" && (
          data.rfis.length === 0 ? <div className="dim">No RFIs raised.</div> : (
            <div className="col" style={{ gap: 12 }}>
              {data.rfis.map((r, i) => (
                <div key={i} className="card pad">
                  <div className="row between wrap" style={{ gap: 10 }}>
                    <span className="row" style={{ gap: 10, alignItems: "baseline", minWidth: 0 }}><span className="badge accent mono" style={{ fontSize: 10.5 }}>{r.ref}</span><span style={{ fontSize: 15, fontWeight: 600 }}>{r.subject}</span></span>
                    <span className="row" style={{ gap: 6 }}>
                      <span className="badge" style={{ fontSize: 9 }}>{r.status === "answered" ? "Answered" : r.status === "closed" ? "Closed" : "Open"}</span>
                      {r.status !== "closed" && <span className="badge" style={{ fontSize: 9 }}>Ball: {r.ballInCourt}</span>}
                    </span>
                  </div>
                  <div style={{ fontSize: 13.5, marginTop: 7, lineHeight: 1.55 }}>{r.question}</div>
                  {r.sheetCode && <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>Sheet {r.sheetCode}{r.dueOn ? ` · reply by ${fmtDate(r.dueOn)}` : ""}</div>}
                  {r.answer && <div className="panel" style={{ padding: "10px 12px", marginTop: 9, background: "color-mix(in srgb, var(--sky) 6%, var(--card))" }}><div className="kicker" style={{ fontSize: 8.5, marginBottom: 3 }}>Answer</div><div style={{ fontSize: 13.5, lineHeight: 1.55 }}>{r.answer}</div></div>}
                </div>
              ))}
            </div>
          )
        )}

        {tab === "submissions" && <SubmissionsTab token={token!} rows={data.submissions ?? []} onDone={refresh} />}
        {tab === "proposals" && <ProposalsTab token={token!} rows={data.proposals ?? []} currency={data.project.currency} onDone={refresh} />}

        {tab === "dates" && (
          data.dates.length === 0 ? <div className="dim">No dates published yet.</div> : (
            <div className="card" style={{ overflow: "hidden" }}>
              {[...data.dates].sort((a, b) => a.date.localeCompare(b.date)).map((e, i) => (
                <div key={i} className="row between" style={{ padding: "12px 16px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
                  <span style={{ fontSize: 13.5 }}>{e.title}</span>
                  <span className="dim" style={{ fontSize: 12.5 }}>{fmtDate(e.date)}</span>
                </div>
              ))}
            </div>
          )
        )}

        <div className="dim" style={{ fontSize: 11.5, marginTop: 26, lineHeight: 1.6 }}>
          This link is personal to you and can be withdrawn at any time. It shows only what {STUDIO.name} has shared for your part of the work — please don't forward it.
        </div>
      </div>
    </Shell>
  );
}

/* ---- the channel back: RFIs, design submissions, cost proposals ---------- */

/** Upload a file through the partner's token — returns {name, url} or null. */
async function partnerUpload(token: string, file: File): Promise<{ name: string; url: string } | null> {
  try {
    const r = await fetch(`/api/file?ptr=${token}&name=${encodeURIComponent(file.name)}`, { method: "POST", headers: { "content-type": file.type || "application/octet-stream" }, body: file });
    if (!r.ok) return null;
    const out = await r.json() as { url?: string };
    return out.url ? { name: file.name, url: out.url } : null;
  } catch { return null; }
}

const STATUS_TONE: Record<string, string> = {
  received: "var(--ink-3)", approved: "var(--ok, #5c7a3f)", revise: "var(--warn, #a07a1e)",
  accepted: "var(--ok, #5c7a3f)", declined: "var(--accent-ink)",
};
const STATUS_TEXT: Record<string, string> = {
  received: "With the studio", approved: "Approved", revise: "Revise and resubmit",
  accepted: "Accepted", declined: "Declined",
};

function RaiseRfi({ token, sheets, onDone }: { token: string; sheets: any[]; onDone: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ subject: "", question: "", sheetCode: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function send() {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`/api/partner/${token}/rfi`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(f) });
      if (!r.ok) { setErr(((await r.json().catch(() => null)) as any)?.error ?? "Couldn't raise the RFI — try again."); return; }
      setF({ subject: "", question: "", sheetCode: "" }); setOpen(false);
      await onDone();
    } finally { setBusy(false); }
  }
  return (
    <div style={{ marginBottom: 18 }}>
      {!open ? (
        <button className="btn accent sm" onClick={() => setOpen(true)}><Icon.plus width={14} />Raise an RFI</button>
      ) : (
        <div className="card pad col" style={{ gap: 10 }}>
          <div className="kicker">New RFI — a question the design must answer</div>
          <input className="input" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject — e.g. Riser location at grid C" />
          <textarea className="textarea" rows={3} value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} placeholder="The question, precisely — what you need decided or clarified, and why it matters for your part of the work." />
          <div className="row wrap" style={{ gap: 10 }}>
            <select className="select" style={{ width: "auto" }} value={f.sheetCode} onChange={(e) => setF({ ...f, sheetCode: e.target.value })}>
              <option value="">No specific sheet</option>
              {sheets.map((s: any, i: number) => <option key={i} value={s.code}>{s.code} — {s.title}</option>)}
            </select>
            <span className="spread" />
            <button className="btn ghost sm" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn accent sm" disabled={busy || !f.subject.trim() || !f.question.trim()} onClick={() => void send()}>
              <Icon.arrowUpRight width={13} />{busy ? "Sending…" : "Send to the studio"}
            </button>
          </div>
          {err && <div style={{ color: "var(--accent-ink)", fontSize: 12.5 }}>{err}</div>}
        </div>
      )}
    </div>
  );
}

function FilePicker({ token, files, setFiles, busy, setBusy }: { token: string; files: { name: string; url: string }[]; setFiles: (f: { name: string; url: string }[]) => void; busy: boolean; setBusy: (b: boolean) => void }) {
  return (
    <div className="row wrap" style={{ gap: 8, alignItems: "center" }}>
      <label className="btn soft sm" style={{ cursor: "pointer" }}>
        <Icon.plus width={13} />{busy ? "Uploading…" : "Attach files"}
        <input type="file" multiple style={{ display: "none" }} onChange={async (e) => {
          const list = Array.from(e.target.files ?? []).slice(0, 12 - files.length);
          if (!list.length) return;
          setBusy(true);
          const up: { name: string; url: string }[] = [];
          for (const file of list) { const r = await partnerUpload(token, file); if (r) up.push(r); }
          setFiles([...files, ...up]);
          setBusy(false);
        }} />
      </label>
      {files.map((x) => (
        <span key={x.url} className="badge" style={{ fontSize: 10.5 }}>
          {x.name}
          <button onClick={() => setFiles(files.filter((y) => y.url !== x.url))} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", marginLeft: 4, padding: 0 }}>×</button>
        </span>
      ))}
    </div>
  );
}

function RowCard({ r, currency }: { r: any; currency?: string }) {
  return (
    <div className="card pad">
      <div className="row between wrap" style={{ gap: 10 }}>
        <span className="row" style={{ gap: 10, alignItems: "baseline", minWidth: 0 }}>
          <span className="badge accent mono" style={{ fontSize: 10.5 }}>{r.ref}</span>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{r.title}</span>
          {typeof r.amount === "number" && <span className="mono" style={{ fontSize: 14 }}>{r.amount.toLocaleString()} {currency ?? ""}</span>}
        </span>
        <span className="badge" style={{ fontSize: 9, color: STATUS_TONE[r.status] ?? "var(--ink-3)", borderColor: "currentColor" }}>{STATUS_TEXT[r.status] ?? r.status}</span>
      </div>
      {r.note && <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.55 }}>{r.note}</div>}
      {(r.files ?? []).length > 0 && (
        <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
          {r.files.map((x: any, i: number) => <a key={i} className="badge" style={{ fontSize: 10.5 }} href={x.url} target="_blank" rel="noreferrer">{x.name}</a>)}
        </div>
      )}
      {r.response && (
        <div className="panel" style={{ padding: "10px 12px", marginTop: 9, background: "color-mix(in srgb, var(--sky) 6%, var(--card))" }}>
          <div className="kicker" style={{ fontSize: 8.5, marginBottom: 3 }}>The studio's answer{r.respondedOn ? ` · ${fmtDate(r.respondedOn)}` : ""}</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>{r.response}</div>
        </div>
      )}
    </div>
  );
}

function SubmissionsTab({ token, rows, onDone }: { token: string; rows: any[]; onDone: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", note: "" });
  const [files, setFiles] = useState<{ name: string; url: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  async function send() {
    setSending(true); setErr("");
    try {
      const r = await fetch(`/api/partner/${token}/submission`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...f, files }) });
      if (!r.ok) { setErr(((await r.json().catch(() => null)) as any)?.error ?? "Couldn't submit — try again."); return; }
      setF({ title: "", note: "" }); setFiles([]); setOpen(false);
      await onDone();
    } finally { setSending(false); }
  }
  return (
    <div className="col" style={{ gap: 12 }}>
      {!open ? (
        <div><button className="btn accent sm" onClick={() => setOpen(true)}><Icon.plus width={14} />Make a design submission</button></div>
      ) : (
        <div className="card pad col" style={{ gap: 10 }}>
          <div className="kicker">Design submission — your drawings / calculations for review</div>
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title — e.g. MEP first-fix layouts, Rev P2" />
          <textarea className="textarea" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="What this submission covers, and anything the studio should look at first." />
          <FilePicker token={token} files={files} setFiles={setFiles} busy={busy} setBusy={setBusy} />
          <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
            <button className="btn ghost sm" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn accent sm" disabled={sending || busy || !f.title.trim() || !files.length} onClick={() => void send()}>
              <Icon.arrowUpRight width={13} />{sending ? "Submitting…" : `Submit${files.length ? ` ${files.length} file${files.length > 1 ? "s" : ""}` : ""}`}
            </button>
          </div>
          {err && <div style={{ color: "var(--accent-ink)", fontSize: 12.5 }}>{err}</div>}
        </div>
      )}
      {rows.length === 0 && !open && <div className="dim">Nothing submitted yet. Your submissions and the studio's responses live here — only you and the studio see them.</div>}
      {rows.map((r, i) => <RowCard key={i} r={r} />)}
    </div>
  );
}

function ProposalsTab({ token, rows, currency, onDone }: { token: string; rows: any[]; currency?: string; onDone: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", amount: "", note: "" });
  const [files, setFiles] = useState<{ name: string; url: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  async function send() {
    setSending(true); setErr("");
    try {
      const r = await fetch(`/api/partner/${token}/proposal`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ title: f.title, note: f.note, amount: +f.amount, files }) });
      if (!r.ok) { setErr(((await r.json().catch(() => null)) as any)?.error ?? "Couldn't submit — try again."); return; }
      setF({ title: "", amount: "", note: "" }); setFiles([]); setOpen(false);
      await onDone();
    } finally { setSending(false); }
  }
  return (
    <div className="col" style={{ gap: 12 }}>
      {!open ? (
        <div><button className="btn accent sm" onClick={() => setOpen(true)}><Icon.plus width={14} />Submit a cost proposal</button></div>
      ) : (
        <div className="card pad col" style={{ gap: 10 }}>
          <div className="kicker">Cost proposal — your price for your part of the work</div>
          <div className="row wrap" style={{ gap: 10 }}>
            <input className="input" style={{ flex: 2, minWidth: 200 }} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="What the price covers — e.g. MEP design & supervision" />
            <input className="input mono" style={{ width: 150 }} inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder={`Amount${currency ? ` (${currency})` : ""}`} />
          </div>
          <textarea className="textarea" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Basis, inclusions and exclusions — plainly." />
          <FilePicker token={token} files={files} setFiles={setFiles} busy={busy} setBusy={setBusy} />
          <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
            <button className="btn ghost sm" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn accent sm" disabled={sending || busy || !f.title.trim() || !(+f.amount > 0)} onClick={() => void send()}>
              <Icon.arrowUpRight width={13} />{sending ? "Submitting…" : "Submit the proposal"}
            </button>
          </div>
          {err && <div style={{ color: "var(--accent-ink)", fontSize: 12.5 }}>{err}</div>}
        </div>
      )}
      {rows.length === 0 && !open && <div className="dim">No proposals yet. Your prices and the studio's answers live here — never visible to other firms.</div>}
      {rows.map((r, i) => <RowCard key={i} r={r} currency={currency} />)}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--paper)" }}>
      <div className="row between" style={{ padding: "14px clamp(18px,4vw,44px)", borderBottom: "1px solid var(--line)" }}>
        <div className="row" style={{ gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center" }}><Icon.logo width={19} /></div>
          <div><div className="serif" style={{ fontSize: 16, lineHeight: 1 }}>{STUDIO.name}</div><div className="kicker" style={{ fontSize: 9 }}>Project access</div></div>
        </div>
      </div>
      <div style={{ display: "grid", placeItems: "start center", padding: "34px clamp(18px,4vw,44px) 80px" }}>{children}</div>
    </div>
  );
}
